import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2, Users, Fuel, ShieldCheck, ScrollText, KeyRound, Loader2, Sparkles,
  Search, PauseCircle, PlayCircle, LogIn, Save, FileText,
} from "lucide-react";

const TABS = [
  { key: "stats", label: "Overview", icon: Sparkles },
  { key: "accounts", label: "Accounts", icon: Building2 },
  { key: "plans", label: "Plans", icon: Fuel },
  { key: "legal", label: "Legal Editor", icon: ScrollText },
  { key: "operator", label: "Operator", icon: FileText },
  { key: "audit", label: "Audit Log", icon: KeyRound },
];

export default function SuperAdmin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("stats");
  if (!user) return null;
  if (!user.is_super_admin) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white grain">
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        <div>
          <p className="ft-overline">PLATFORM · SUPER ADMIN</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none mt-1">
            <ShieldCheck className="inline mr-2 text-[#FF5A1F]" size={40} /> Command Console
          </h1>
          <p className="mt-3 text-[#A3A3A3]">Signed in as <span className="text-white font-mono">{user.email}</span></p>
        </div>
        <nav className="flex gap-1 border-b border-[#262626] overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-3 text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2 whitespace-nowrap ${tab === t.key ? "text-white border-b-2 border-[#FF5A1F]" : "text-[#A3A3A3] hover:text-white"}`}
                data-testid={`sa-tab-${t.key}`}
              >
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </nav>
        {tab === "stats" && <StatsTab />}
        {tab === "accounts" && <AccountsTab />}
        {tab === "plans" && <PlansTab />}
        {tab === "legal" && <LegalTab />}
        {tab === "operator" && <OperatorTab />}
        {tab === "audit" && <AuditTab />}
      </div>
    </div>
  );
}

function StatsTab() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/super/stats").then((r) => setS(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  if (!s) return <div className="text-[#737373]"><Loader2 className="inline animate-spin" size={16} /> Loading…</div>;
  const tiles = [
    ["Accounts", s.accounts_total, `${s.accounts_new_30d} new · ${s.accounts_suspended} suspended`],
    ["Users", s.users_total, `${s.users_super} super admin`],
    ["Vehicles", s.vehicles_total, "across all tenants"],
    ["Refuels (30d)", s.refuels_30d, `${s.refuels_total} total`],
    ["Paid Subscriptions", s.active_paid_subscriptions, "active + trialing"],
    ["MRR (₹)", s.monthly_recurring_revenue, "monthly recurring revenue"],
    ["Documents", s.documents_total, "RC/Insurance/PUC"],
    ["Audit Events (30d)", s.audit_events_30d, "platform-wide actions"],
  ];
  return (
    <div className="space-y-6" data-testid="sa-stats">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {tiles.map(([label, value, sub]) => (
          <div key={label} className="ft-card p-5">
            <p className="ft-overline">{label}</p>
            <p className="font-mono text-3xl font-medium mt-2 text-white">{value}</p>
            <p className="text-xs text-[#737373] mt-1">{sub}</p>
          </div>
        ))}
      </div>
      <MfaPanel />
    </div>
  );
}

function MfaPanel() {
  const [me, setMe] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);
  const start = async () => {
    const { data } = await api.get("/admin/mfa/setup");
    setSetup(data);
  };
  const enable = async () => {
    setBusy(true);
    try {
      await api.post("/admin/mfa/enable", { secret: setup.secret, code });
      toast.success("MFA enabled — you'll be prompted for a code on next sign-in");
      setSetup(null); setCode("");
      const { data } = await api.get("/auth/me");
      setMe(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    const c = prompt("Enter current 6-digit code to disable MFA:");
    if (!c) return;
    try {
      await api.post("/admin/mfa/disable", { code: c });
      toast.success("MFA disabled");
      const { data } = await api.get("/auth/me");
      setMe(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!me) return null;
  return (
    <div className="ft-card p-6 space-y-3" data-testid="sa-mfa">
      <p className="ft-overline flex items-center gap-2"><ShieldCheck size={14} /> TWO-FACTOR AUTH (TOTP)</p>
      <p className="text-sm text-[#A3A3A3]">Status: <span className={`font-mono ${me.mfa_enabled ? "text-emerald-400" : "text-red-400"}`}>{me.mfa_enabled ? "ENABLED" : "DISABLED"}</span></p>
      {!me.mfa_enabled ? (
        !setup ? (
          <button onClick={start} className="bg-[#FF5A1F] text-black font-bold px-4 py-2" data-testid="mfa-start">Enable MFA</button>
        ) : (
          <div className="space-y-3 border border-[#262626] p-4 max-w-md">
            <p className="text-sm">1. Scan this URI with Google Authenticator / Authy:</p>
            <p className="font-mono text-xs break-all text-[#FF5A1F]" data-testid="mfa-otpauth">{setup.otpauth_url}</p>
            <p className="text-sm">2. Or paste this secret manually: <span className="font-mono text-[#FF5A1F]">{setup.secret}</span></p>
            <p className="text-sm">3. Enter the 6-digit code:</p>
            <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g,"").slice(0,6))} className="w-40 bg-[#0A0A0A] border border-[#262626] px-3 py-2 font-mono text-lg tracking-widest" data-testid="mfa-code" placeholder="123456"/>
            <div className="flex gap-2">
              <button onClick={enable} disabled={busy || code.length !== 6} className="bg-[#FF5A1F] text-black font-bold px-4 py-2 disabled:opacity-60" data-testid="mfa-enable">Verify &amp; enable</button>
              <button onClick={() => { setSetup(null); setCode(""); }} className="border border-[#262626] px-4 py-2">Cancel</button>
            </div>
          </div>
        )
      ) : (
        <button onClick={disable} className="border border-red-500/40 text-red-300 px-4 py-2 font-semibold" data-testid="mfa-disable">Disable MFA</button>
      )}
    </div>
  );
}

function AccountsTab() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/super/accounts", { params: q ? { q } : {} });
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleStatus = async (a) => {
    const next = a.status === "suspended" ? "active" : "suspended";
    if (!window.confirm(`${next === "suspended" ? "Suspend" : "Reactivate"} ${a.name}?`)) return;
    try {
      await api.post(`/super/accounts/${a.id}/status`, { status: next });
      toast.success(`Account ${next}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const impersonate = async (a) => {
    if (!window.confirm(`Sign in as ${a.name}'s owner? This is audit-logged.`)) return;
    try {
      const { data } = await api.post(`/super/impersonate/${a.id}`);
      localStorage.setItem("ft_token", data.token);
      toast.success(`Impersonating ${data.as_email}`);
      window.location.href = "/";
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const assignPlan = async (a) => {
    const plan_id = prompt("Assign plan id (free / pro / business):", a.subscription?.plan_id || "free");
    if (!plan_id) return;
    try {
      await api.post(`/super/accounts/${a.id}/subscription`, { plan_id });
      toast.success(`Plan assigned: ${plan_id}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="sa-accounts">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by account name or billing email…"
            className="w-full pl-9 pr-3 py-2 bg-[#0A0A0A] border border-[#262626] font-mono text-sm focus:border-[#FF5A1F] outline-none"
            data-testid="sa-accounts-search"
          />
        </div>
        <button onClick={load} className="border border-[#262626] px-3 py-2 hover:bg-[#141414]">Search</button>
      </div>
      <div className="ft-card p-0 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#262626] text-left text-xs">
              <th className="ft-overline py-3 px-4">ACCOUNT</th>
              <th className="ft-overline py-3 px-4">PLAN</th>
              <th className="ft-overline py-3 px-4">USAGE</th>
              <th className="ft-overline py-3 px-4">STATUS</th>
              <th className="py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {busy ? (
              <tr><td colSpan="5" className="p-8 text-center text-[#737373]"><Loader2 className="inline animate-spin" size={16} /></td></tr>
            ) : rows.map((a) => (
              <tr key={a.id} className="border-b border-[#262626] hover:bg-[#141414]">
                <td className="py-3 px-4">
                  <p className="font-mono text-white text-sm font-bold">{a.name}</p>
                  <p className="text-xs text-[#737373] mt-0.5">{a.billing_email || "—"}</p>
                </td>
                <td className="py-3 px-4">
                  <span className="text-xs font-mono uppercase tracking-wider text-[#FF5A1F]">{a.subscription?.plan_id || "free"}</span>
                  <p className="text-[10px] text-[#737373]">{a.subscription?.status}</p>
                </td>
                <td className="py-3 px-4 text-xs text-[#A3A3A3] font-mono">
                  {a.user_count}u · {a.vehicle_count}v · {a.refuel_count}f
                </td>
                <td className="py-3 px-4">
                  <span className={`text-xs font-mono uppercase ${a.status === "active" ? "text-emerald-400" : "text-red-400"}`}>
                    {a.status}
                  </span>
                </td>
                <td className="py-3 px-4 text-right space-x-2">
                  <button onClick={() => assignPlan(a)} className="text-xs border border-[#262626] px-2 py-1 hover:bg-[#141414] hover:border-[#FF5A1F]/50" data-testid={`sa-plan-${a.id}`}>Plan</button>
                  <button onClick={() => impersonate(a)} className="text-xs border border-[#262626] px-2 py-1 hover:bg-[#141414] hover:border-[#FF5A1F]/50 inline-flex items-center gap-1" data-testid={`sa-imp-${a.id}`}><LogIn size={11} /> As Owner</button>
                  <button onClick={() => toggleStatus(a)} className="text-xs border border-[#262626] px-2 py-1 hover:bg-[#141414] inline-flex items-center gap-1" data-testid={`sa-status-${a.id}`}>
                    {a.status === "active" ? <><PauseCircle size={11} /> Suspend</> : <><PlayCircle size={11} /> Reactivate</>}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PlansTab() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => {
    const { data } = await api.get("/super/plans");
    setPlans(data || []);
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      await api.post("/super/plans", editing);
      toast.success("Plan saved");
      setEditing(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (p) => {
    if (p.id === "free") return toast.error("Free plan cannot be deleted");
    if (!window.confirm(`Delete plan ${p.name}?`)) return;
    try {
      await api.delete(`/super/plans/${p.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-4" data-testid="sa-plans">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {plans.map((p) => (
          <div key={p.id} className="ft-card p-5 space-y-2">
            <p className="ft-overline">{p.id.toUpperCase()}</p>
            <h3 className="font-display text-2xl font-bold">{p.name}</h3>
            <p className="font-mono text-3xl">₹{p.price_monthly}<span className="text-sm text-[#737373]">/mo</span></p>
            <p className="text-xs text-[#A3A3A3]">Vehicles: {p.vehicle_limit} · Members: {p.member_limit}</p>
            <p className="text-xs text-[#A3A3A3]">AI: {p.ai_ocr_limit} OCR + {p.ai_ask_limit} Ask · Storage: {p.storage_mb} MB</p>
            <p className="text-xs text-[#A3A3A3]">Commercial: {p.commercial_ok ? "Yes" : "No"}</p>
            <div className="flex gap-2 pt-2">
              <button onClick={() => setEditing({...p})} className="text-xs border border-[#262626] px-3 py-1 hover:border-[#FF5A1F]/50" data-testid={`plan-edit-${p.id}`}>Edit</button>
              <button onClick={() => del(p)} className="text-xs border border-[#262626] px-3 py-1 text-[#A3A3A3] hover:text-red-400" data-testid={`plan-delete-${p.id}`}>Delete</button>
            </div>
          </div>
        ))}
      </div>
      <button onClick={() => setEditing({id:"",name:"",price_monthly:0,price_annual:0,currency:"INR",tax_pct:18,trial_days:0,vehicle_limit:1,member_limit:5,commercial_ok:false,ai_ocr_limit:30,ai_ask_limit:20,storage_mb:200,export_enabled:true,reports_enabled:true,feature_flags:{},is_default:false,active:true})} className="bg-[#FF5A1F] text-black px-4 py-2 font-bold" data-testid="plan-new">+ New plan</button>
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-6" onClick={() => setEditing(null)}>
          <div className="ft-card p-6 max-w-2xl w-full space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl font-bold">Edit plan</h3>
            <div className="grid grid-cols-2 gap-3">
              {[["id","ID (slug)"],["name","Name"],["price_monthly","₹/mo"],["price_annual","₹/year"],["currency","Currency"],["tax_pct","Tax %"],["trial_days","Trial days"],["vehicle_limit","Vehicles"],["member_limit","Members"],["ai_ocr_limit","AI OCR limit"],["ai_ask_limit","AI Ask limit"],["storage_mb","Storage MB"]].map(([k,l]) => (
                <div key={k}>
                  <label className="ft-overline block mb-1">{l}</label>
                  <input value={editing[k] ?? ""} onChange={(e) => setEditing({...editing, [k]: typeof editing[k] === "number" ? Number(e.target.value) : e.target.value})} className="w-full bg-[#0A0A0A] border border-[#262626] px-2 py-1 font-mono text-sm" data-testid={`plan-field-${k}`}/>
                </div>
              ))}
            </div>
            <div className="flex gap-4 text-xs">
              {["commercial_ok","export_enabled","reports_enabled","is_default","active"].map((k) => (
                <label key={k} className="inline-flex items-center gap-2"><input type="checkbox" checked={!!editing[k]} onChange={(e) => setEditing({...editing, [k]: e.target.checked})} data-testid={`plan-bool-${k}`}/> {k}</label>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={save} className="bg-[#FF5A1F] text-black font-bold px-4 py-2 inline-flex items-center gap-2" data-testid="plan-save"><Save size={14}/> Save</button>
              <button onClick={() => setEditing(null)} className="border border-[#262626] px-4 py-2">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LegalTab() {
  const [pages, setPages] = useState([]);
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = async () => {
    const { data } = await api.get("/legal");
    setPages(data || []);
    if (data && data.length && !active) setActive(data[0]);
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!active) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/legal/${active.slug}`, { title: active.title, content_md: active.content_md });
      setActive(data);
      toast.success("Legal page saved");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="grid grid-cols-12 gap-4" data-testid="sa-legal">
      <aside className="col-span-12 md:col-span-3 ft-card p-2 max-h-[70vh] overflow-y-auto">
        {pages.map((p) => (
          <button
            key={p.slug}
            onClick={() => setActive(p)}
            className={`w-full text-left px-3 py-2 text-sm hover:bg-[#141414] ${active?.slug === p.slug ? "bg-[#141414] text-[#FF5A1F]" : "text-[#A3A3A3]"}`}
            data-testid={`legal-tab-${p.slug}`}
          >
            <p className="font-semibold truncate">{p.title}</p>
            <p className="text-[10px] font-mono text-[#737373]">{p.slug}</p>
          </button>
        ))}
      </aside>
      <div className="col-span-12 md:col-span-9 ft-card p-5 space-y-3">
        {!active ? <p className="text-[#737373]">Select a page…</p> : (
          <>
            <div className="flex justify-between items-start gap-3 flex-wrap">
              <input value={active.title || ""} onChange={(e) => setActive({...active, title: e.target.value})} className="text-2xl font-display font-black bg-[#0A0A0A] border border-[#262626] px-3 py-2 flex-1 min-w-[220px]" data-testid="legal-title-input"/>
              <div className="flex gap-2">
                <a href={`/legal/${active.slug}`} target="_blank" rel="noopener" className="text-xs text-[#A3A3A3] border border-[#262626] px-3 py-2 hover:border-[#FF5A1F]/50">Preview</a>
                <button onClick={save} disabled={saving} className="bg-[#FF5A1F] text-black font-bold px-4 py-2 inline-flex items-center gap-2" data-testid="legal-save"><Save size={14}/> Save</button>
              </div>
            </div>
            <p className="text-xs text-[#737373] font-mono">/legal/{active.slug}</p>
            <textarea
              value={active.content_md || ""}
              onChange={(e) => setActive({...active, content_md: e.target.value})}
              rows={22}
              placeholder={`# ${active.title}\n\nWrite your Markdown here. Headings, **bold**, *italic*, links [x](https://...), and\n- bullet lists\nare supported.`}
              className="w-full bg-[#0A0A0A] border border-[#262626] font-mono text-sm p-4 focus:border-[#FF5A1F] outline-none"
              data-testid={`legal-editor-${active.slug}`}
            />
            <p className="text-xs text-[#737373]">Last updated: {String(active.updated_at || "").slice(0, 19).replace("T", " ")}</p>
          </>
        )}
      </div>
    </div>
  );
}

function OperatorTab() {
  const [form, setForm] = useState({legal_entity:"",registered_address:"",support_email:"",grievance_contact:"",jurisdiction:"India",effective_date:""});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/legal/config/operator").then((r) => setForm({...form, ...(r.data || {})})).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/legal/config/operator", form);
      toast.success("Operator info saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="ft-card p-6 space-y-4 max-w-2xl" data-testid="sa-operator">
      <p className="ft-overline">LEGAL ENTITY · APPEARS ON EVERY LEGAL PAGE FOOTER</p>
      {[["legal_entity","Legal entity name"],["registered_address","Registered address"],["support_email","Support email"],["grievance_contact","Grievance contact (name + phone/email)"],["jurisdiction","Jurisdiction"],["effective_date","Effective date"]].map(([k,l]) => (
        <div key={k}>
          <label className="ft-overline block mb-1">{l}</label>
          <input value={form[k] || ""} onChange={(e) => setForm({...form, [k]: e.target.value})} className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 font-mono text-sm" data-testid={`operator-${k}`}/>
        </div>
      ))}
      <button onClick={save} disabled={saving} className="bg-[#FF5A1F] text-black font-bold px-4 py-2 inline-flex items-center gap-2" data-testid="operator-save"><Save size={14}/> Save</button>
    </div>
  );
}

function AuditTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/super/audit", { params: { limit: 200 } }).then((r) => setRows(r.data || [])); }, []);
  return (
    <div className="ft-card p-0 overflow-hidden" data-testid="sa-audit">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-[#262626] text-left">
            <th className="ft-overline py-3 px-4">WHEN</th>
            <th className="ft-overline py-3 px-4">ACTOR</th>
            <th className="ft-overline py-3 px-4">ACTION</th>
            <th className="ft-overline py-3 px-4">TARGET</th>
            <th className="ft-overline py-3 px-4">DETAILS</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.id} className="border-b border-[#262626] hover:bg-[#141414]">
              <td className="py-2 px-4 font-mono text-[#A3A3A3]">{String(e.created_at || "").slice(0, 19).replace("T"," ")}</td>
              <td className="py-2 px-4 font-mono text-white">{e.actor_email || e.actor_user_id?.slice(0,8) || "—"}</td>
              <td className="py-2 px-4 font-mono text-[#FF5A1F]">{e.action}</td>
              <td className="py-2 px-4 font-mono text-[#A3A3A3]">{e.target_collection}:{(e.target_id || "").slice(0,8)}</td>
              <td className="py-2 px-4 font-mono text-[#737373]">{JSON.stringify(e.details || {}).slice(0, 80)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
